"use strict";
import { jsx, jsxs } from "react/jsx-runtime";
import { createContext, forwardRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import { animated, useSpring } from "react-spring";
import { Button, Buttons } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { BoundsObserver } from "capitalrx-components-2.0/lib/utilities";
import classNames from "classnames";
import { ActionBars, AgentChatbotFlyout, AgentChatContextProvider, BreadcrumbItems, BreadcrumbRoot, ContentChannel, MenuBar, TaskFlyout, useContentChannel } from "./components";
import styles from "./ContentLayout.module.css";
function noOp() {
}
const ContentLayoutContext = createContext({
  layoutType: "auto",
  setLayoutType: noOp
});
export function useSetLayout() {
  const { setLayoutType } = useContext(ContentLayoutContext);
  useEffect(() => {
    return () => {
      setLayoutType("auto");
    };
  }, []);
  return useMemo(() => setLayoutType, [setLayoutType]);
}
export function useLayout() {
  const { layoutType } = useContext(ContentLayoutContext);
  return layoutType;
}
export const ContentLayout = (props) => {
  const { fullscreen = false, children } = props;
  const location = useLocation();
  const [layoutType, setLayoutType] = useState("auto");
  useEffect(() => {
    if (fullscreen) {
      setLayoutType((current) => {
        if (!current.startsWith("fullscreen")) return "fullscreen";
        return current;
      });
    }
    return () => {
      setLayoutType("auto");
    };
  }, [fullscreen]);
  const contextValue = useMemo(
    () => ({
      layoutType,
      setLayoutType
    }),
    [layoutType]
  );
  return /* @__PURE__ */ jsx("div", { className: classNames("content-layout", styles.root, { [styles.nonFullscreen]: !layoutType.startsWith("fullscreen") }), children: /* @__PURE__ */ jsx(ContentLayoutContext.Provider, { value: contextValue, children: /* @__PURE__ */ jsxs(BreadcrumbRoot, { children: [
    /* @__PURE__ */ jsx(MenuBar, {}),
    /* @__PURE__ */ jsxs(MainContentContainer, { children: [
      /* @__PURE__ */ jsx(ActionBars, {}),
      /* @__PURE__ */ jsx(LayoutContainer, { children })
    ] }, location.pathname)
  ] }) }) });
};
const MainContentContainer = (props) => {
  const { children } = props;
  const animStyles = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    delay: 50,
    config: { duration: 500 }
  });
  return /* @__PURE__ */ jsx(animated.div, { className: "main-content-container", style: animStyles, children });
};
const ExpandContractButtonAppearance = {
  ...Buttons.Functional.Tertiary.Large,
  padding: {
    horizontal: 4,
    vertical: 4
  },
  icon: {
    size: "regular"
  }
};
const SideBarContainer = (props) => {
  const { id, type, bounds, children } = props;
  const { top } = bounds;
  const [expanded, setExpanded] = useState(true);
  const childrenContainerRef = useRef(null);
  const [width, setWidth] = useState(null);
  const [resizeObserver] = useState(() => new ResizeObserver((entries) => {
    var _a, _b, _c;
    const entry = entries[0];
    if (!entry) return;
    if (!((_c = (_b = (_a = entry.target) == null ? void 0 : _a.firstChild) == null ? void 0 : _b.hasChildNodes) == null ? void 0 : _c.call(_b))) return;
    setWidth(entry.contentRect.width);
  }));
  useEffect(() => {
    var _a;
    const storageKey = `sb_${id}`;
    setExpanded(((_a = localStorage.getItem(storageKey)) != null ? _a : "true") === "true");
  }, [id]);
  const style = useMemo(() => ({ top, height: `calc(100vh - ${top}px)` }), [top]);
  const scrollAreaStyle = useMemo(() => {
    if (width === null) return {};
    return expanded ? { width: width + 48 } : { width: 44 };
  }, [expanded, width]);
  useEffect(() => {
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  const toggleHandler = useCallback(() => {
    setExpanded((x) => {
      const result = !x;
      const storageKey = `sb_${id}`;
      localStorage.setItem(storageKey, result ? "true" : "false");
      return result;
    });
  }, [id]);
  const childrenRefHandler = useCallback((ref) => {
    if (childrenContainerRef.current !== ref && childrenContainerRef.current) {
      resizeObserver.unobserve(childrenContainerRef.current);
      childrenContainerRef.current = null;
    }
    if (ref) resizeObserver.observe(ref);
    childrenContainerRef.current = ref;
  }, []);
  return /* @__PURE__ */ jsxs(Stack, { className: classNames("sidebar-container", type, { expanded, contracted: !expanded }), style, children: [
    /* @__PURE__ */ jsxs(Shelf, { reverse: type === "right", children: [
      /* @__PURE__ */ jsx(Shelf.Spacer, {}),
      /* @__PURE__ */ jsx(Button, { appearance: ExpandContractButtonAppearance, icon: expanded ? "x" : "menu-bars", onClick: toggleHandler })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "scroll-area", style: scrollAreaStyle, children: /* @__PURE__ */ jsx("div", { ref: childrenRefHandler, className: "children", children }) })
  ] });
};
const BodyContainer = forwardRef((props, ref) => {
  const { bounds, children } = props;
  const actionBars = useContentChannel(null, "app-action-bars");
  const { layoutType } = useContext(ContentLayoutContext);
  const [tasksVisible, setTasksVisible] = useState(false);
  const className = useMemo(() => {
    const width = bounds.width - 80;
    if (width <= 320) return "mobile";
    else if (width <= 576) return "mobile-landscape";
    else if (width <= 768) return "tablet";
    else if (width <= 992) return "laptop";
    else if (width <= 1024) return "desktop-narrow";
    else if (width <= 1200) return "desktop";
    else return "desktop-wide";
  }, [layoutType, bounds.width]);
  useEffect(() => {
    if (tasksVisible) document.body.classList.add("has-tasks");
    else document.body.classList.remove("has-tasks");
  }, [tasksVisible]);
  let layoutClassName = "auto";
  if (layoutType.startsWith("fixed")) {
    layoutClassName = "fixed";
  } else if (layoutType.startsWith("auto")) {
    layoutClassName = "auto";
  }
  return /* @__PURE__ */ jsxs(AgentChatContextProvider, { children: [
    /* @__PURE__ */ jsx(TaskFlyout, { onVisibilityChange: setTasksVisible }),
    /* @__PURE__ */ jsx(AgentChatbotFlyout, { hasTasks: tasksVisible }),
    /* @__PURE__ */ jsx("div", { ref, className: classNames("body-container", { "unpadded": layoutType.endsWith("unpadded") }), children: /* @__PURE__ */ jsxs("div", { className: classNames("body-layout", layoutClassName, className), children: [
      actionBars.length === 0 && /* @__PURE__ */ jsx(BreadcrumbItems, {}),
      children
    ] }) })
  ] });
});
const ShiftDistance = -64;
const BodyShadow = (props) => {
  const { bounds } = props;
  const { layoutType } = useContext(ContentLayoutContext);
  const styles2 = useMemo(() => {
    const { top, left, right, bottom } = bounds;
    return {
      top,
      left: left === 0 ? ShiftDistance : left,
      right: right === 0 ? ShiftDistance : right,
      bottom: bottom === 0 ? ShiftDistance : bottom
    };
  }, [bounds]);
  if (layoutType.startsWith("fullscreen") || layoutType.endsWith("unpadded")) {
    return null;
  }
  return /* @__PURE__ */ jsx("div", { className: "body-shadow", style: styles2 });
};
const LayoutContainer = (props) => {
  const { children } = props;
  const [currentBounds, setCurrentBounds] = useState({ top: 0, left: 0, right: 0, bottom: 0, width: 0 });
  const boundsObserver = useMemo(
    () => new BoundsObserver((entries) => {
      const [entry] = entries;
      const { target } = entry;
      const { offsetParent } = target;
      const parentWidth = offsetParent === null ? window.innerWidth : offsetParent.offsetWidth;
      const parentHeight = offsetParent === null ? window.innerHeight : offsetParent.offsetHeight;
      setCurrentBounds((previousBounds) => {
        const { top, left, right, bottom, width } = previousBounds;
        const newTop = entry.offsetTop;
        const newLeft = entry.offsetLeft;
        const newRight = parentWidth - entry.bounds.width - entry.offsetLeft;
        const newBottom = parentHeight - entry.bounds.height - entry.offsetTop;
        const newWidth = parentWidth - (newLeft + newRight);
        if (top !== newTop || left !== newLeft || right !== newRight || bottom !== newBottom || width !== newWidth) {
          return {
            top: newTop,
            left: newLeft,
            right: newRight,
            bottom: newBottom,
            width: newWidth
          };
        }
        return previousBounds;
      });
    }),
    []
  );
  const containerRefHandler = useCallback(
    (ref) => {
      if (ref) boundsObserver.observe(ref);
      else boundsObserver.disconnect();
    },
    [boundsObserver]
  );
  return /* @__PURE__ */ jsxs("div", { className: "layout-container", children: [
    /* @__PURE__ */ jsx(LeftSideBars, { bounds: currentBounds }),
    /* @__PURE__ */ jsx(BodyContainer, { ref: containerRefHandler, bounds: currentBounds, children }),
    /* @__PURE__ */ jsx(RightSideBars, { bounds: currentBounds }),
    /* @__PURE__ */ jsx(BodyShadow, { bounds: currentBounds })
  ] });
};
const LeftSideBars = (props) => {
  const { bounds } = props;
  const { layoutType } = useContext(ContentLayoutContext);
  if (layoutType.startsWith("fullscreen")) {
    return null;
  }
  return /* @__PURE__ */ jsx(ContentChannel, { id: "app-sidebar-left", children: (children, index, _, __, params) => {
    var _a;
    let sideBarParams = void 0;
    if (typeof params === "object" && params && "id" in params)
      sideBarParams = params;
    return /* @__PURE__ */ jsx(SideBarContainer, { id: (_a = sideBarParams == null ? void 0 : sideBarParams.id) != null ? _a : `left_${index}`, type: "left", index, bounds, children });
  } });
};
const RightSideBars = (props) => {
  const { bounds } = props;
  const { layoutType } = useContext(ContentLayoutContext);
  if (layoutType.startsWith("fullscreen")) {
    return null;
  }
  return /* @__PURE__ */ jsx(ContentChannel, { id: "app-sidebar-right", children: (children, index, _, __, params) => {
    var _a;
    let sideBarParams = void 0;
    if (typeof params === "object" && params && "id" in params)
      sideBarParams = params;
    return /* @__PURE__ */ jsx(SideBarContainer, { id: (_a = sideBarParams == null ? void 0 : sideBarParams.id) != null ? _a : `right_${index}`, type: "right", index, bounds, children });
  } });
};
